import { Box, Button, Divider, FormGroup, FormHelperText, Grid, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { createUser } from 'src/shared/services/userService';
import { InfoRounded } from '@mui/icons-material';
import { Colors } from 'src/shared/components/styles/colors';
import { useGlobals } from 'src/shared/contexts/appContext';
import { SysProductYvNameEnum } from 'src/shared/models/enums/sysProductEnums';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { RegexHelper } from 'src/shared/helpers/regexHelper';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { FormErrorSummary } from 'src/shared/components/form/formErrorSummary';
import { UserRequest } from '../models/userRequest';
import { UserCreateModalPermissionButton } from './userCreateModalPermissionButton';
import { SysProductResponse } from '../models/sysProductResponse';
import { UserCreateConfirm } from './userCreateConfirm';
import { UserDistrictAssociationDropdown } from './userDistrictAssociationDropdown';
import { UserCreateInvoiceGroupSummary } from './userCreateInvoiceGroupSummary';

const excludedSysProducts = [
  SysProductYvNameEnum.DocumentArchive.toLowerCase(),
  SysProductYvNameEnum.ImageArchive.toLowerCase(),
  SysProductYvNameEnum.Recruit.toLowerCase(),
  SysProductYvNameEnum.Organisation.toLowerCase(),
  SysProductYvNameEnum.WebTool.toLowerCase(),
  SysProductYvNameEnum.Settings.toLowerCase(),
];

type UserCreateModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  preSelectedAssociation?: number;
  preSelectedDistrict?: number;
};

export const UserCreateModal = (props: UserCreateModalProps) => {
  const { modalOpen, onSave, onClose, preSelectedAssociation, preSelectedDistrict } = props;

  const { t } = useTranslation(['common', 'user']);
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [authority, setAuthority] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sysProducts, setSysProducts] = useState<SysProductResponse[]>([]);

  const onHandleClose = () => {
    onClose();
  };

  const onHandleSave = useCallback(() => {
    onSave();
  }, [onSave]);

  const requiredMessage = t('common:validationMessages.required');
  const loginMethodMessage = t('user:createUser.loginMethodRequired');
  const personnummerFormatMessage = t('user:createUser.personalIdentityNrFormat');
  const personNrFormat = RegexHelper.personalNumberWithDash;

  // 'authority' valideras beroende på värdet av 'isChecked'.
  const userRequestSysProductSchema = yup.object().shape({
    product_id: yup.number().min(1, requiredMessage).required(requiredMessage),
    authority: yup
      .number()
      .min(0, requiredMessage)
      .test('authority-condition.newUser', requiredMessage, (value, context) => {
        const { isChecked } = context.parent;
        if (isChecked) {
          return value !== undefined && value >= 1;
        }
        return value === undefined || value === 0;
      }),
    isChecked: yup.boolean().required(),
  });

  const CreateUserSchema: yup.Schema<UserRequest> = yup
    .object({
      firstName: yup.string().required(requiredMessage),
      lastName: yup.string().required(requiredMessage),
      email: yup
        .string()
        .trim()
        .matches(RegexHelper.emailAddress, {
          message: t('common:validationMessages.email'),
          excludeEmptyString: true,
        })
        .required(requiredMessage),

      comment: yup.string().optional(),

      emailLoginEnabled: yup.boolean().required().default(false),
      bankIdLoginEnabled: yup.boolean().required().default(false),

      districts: yup.array<District>(),
      associations: yup.array<Association>(),

      sysProducts: yup
        .array()
        .min(1, t('common:validationMessages.required'))
        .of(userRequestSysProductSchema)
        .test('minimum-one-modul', t('user:createUser.moduleSelectionError'), (products) => {
          return products?.some((product) => product.isChecked === true);
        })
        .required(),

      invoiceGroups: yup.array<InvoiceGroup>(),

      // Personnummer: krävs om BankID-vald, annars valfritt men måste matcha om ifyllt
      personalIdentityNumber: yup
        .string()
        .transform((v) => (typeof v === 'string' ? v.trim() : v))
        .when('bankIdLoginEnabled', {
          is: true,
          then: (s) => s.required(requiredMessage).matches(personNrFormat, personnummerFormatMessage),
          otherwise: (s) =>
            s.notRequired().matches(personNrFormat, {
              message: personnummerFormatMessage,
              excludeEmptyString: true,
            }),
        }),
    })
    // Minst en inloggningsmetod måste väljas
    .test({
      name: 'at-least-one-login-method',
      message: loginMethodMessage,
      test(value, ctx) {
        if (!value) return false;
        const ok = !!(value.emailLoginEnabled || value.bankIdLoginEnabled);
        // Lägg felet på RHF:s root så du kan läsa errors.root?.message i UI
        return ok || ctx.createError({ path: 'root', message: loginMethodMessage });
      },
    });

  const formMethods = useForm<UserRequest>({
    resolver: yupResolver(CreateUserSchema),
    mode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      comment: '',
      personalIdentityNumber: '',
      emailLoginEnabled: false,
      bankIdLoginEnabled: false,
      districts: [],
      associations: [],
      sysProducts: [],
      invoiceGroups: [],
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = formMethods;

  const { fields: sysProductFields, append: appendSysProduct } = useFieldArray({
    control,
    name: 'sysProducts',
  });

  useFieldArray({
    control,
    name: 'invoiceGroups',
  });

  const invoiceIsChecked = () => {
    const form = watch('sysProducts') || [];
    const foundItem = form.find((item) => item.yvName?.toLowerCase() === SysProductYvNameEnum.Invoice.toLowerCase());
    return foundItem ? foundItem.isChecked : false;
  };

  const onSubmit = useCallback(
    async (data: UserRequest) => {
      setLoading(true);
      try {
        const requestData = {
          ...data,
          userName: data.email,
          sysProducts: data.sysProducts
            .filter((x) => x.isChecked)
            .map((x) => {
              return { product_id: x.product_id, authority: x.authority };
            }),
        };
        const result = await createUser(requestData);
        if (isMounted()) {
          ConsoleHelper.log('res createUser', result);
          toast.success(<>{t('user:createUser.success')}</>);
          ConsoleHelper.log(requestData);
          onHandleSave();
        }
      } catch (e: any) {
        if (isMounted()) {
          const errorMessage = e?.data?.messages;
          toast.error(<>{errorMessage}</>);
        }
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, onHandleSave, t],
  );

  const handleSubmitClick = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  useEffect(() => {
    if (currentUser) {
      // Filtrerar bort ofärdiga moduler från UI
      const filteredResults = currentUser?.sysProducts.filter((item) => !excludedSysProducts.includes(item.yvName.toLowerCase()));
      setSysProducts(currentUser?.sysProducts);
      if (!(sysProductFields.length > 0)) {
        filteredResults.forEach((item) => {
          appendSysProduct({ product_id: item.product_id, authority: 0, isChecked: false, yvName: item.yvName });
        });
      }
      const findAuthority = filteredResults.find((item) => item.yvName.toLowerCase() === SysProductYvNameEnum.System.toLowerCase());
      if (findAuthority) {
        setAuthority(findAuthority?.authority);
      }
    }
  }, [appendSysProduct, currentUser, sysProductFields.length]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogTitle={t('user:createUser.modalHeader')}
      maxWidth='lg'
      showDialogActions={false}
      padding={0}
      dialogContent={
        <FormLoaderWrapper loading={loading}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ p: 3 }}>
                <GridWrapper>
                  <Grid item xs={12} md={6}>
                    <GridWrapper>
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        <ControlledTextField
                          required
                          name='email'
                          label={t('user:createUser.email')}
                          helperText={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <InfoRounded sx={{ fontSize: '0.8rem', mr: 0.5, mb: '3px', color: Colors.ChipColors.Blue }} />
                              <span>{t('user:createUser.emailDescription')}</span>
                            </Box>
                          }
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <ControlledTextField required name='firstName' label={t('user:createUser.firstName')} />
                      </Grid>
                      <Grid item xs={6}>
                        <ControlledTextField required name='lastName' label={t('user:createUser.lastName')} />
                      </Grid>

                      <Grid item xs={12}>
                        <ControlledTextField name='personalIdentityNumber' label={t('user:createUser.personalIdentityNumber')} />
                      </Grid>

                      <Grid item xs={12} mb={1}>
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent='space-between'
                          alignItems='flex-start'
                          spacing={2}
                          sx={{ width: '100%' }}
                        >
                          <Typography sx={{ whiteSpace: 'nowrap' }}>{t('user:createUser.loginMethods')}</Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: { xs: 'flex-start', sm: 'flex-end' },
                              minWidth: 0,
                            }}
                          >
                            <FormGroup
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                mb: -1,
                              }}
                            >
                              <ControlledCheckboxNew name='emailLoginEnabled' label={t('user:createUser.emailLoginEnabled')} />
                              <ControlledCheckboxNew name='bankIdLoginEnabled' label={t('user:createUser.bankIdLoginEnabled')} />
                            </FormGroup>

                            <FormHelperText error>{errors.root?.message || ' '}</FormHelperText>
                          </Box>
                        </Stack>
                      </Grid>

                      <Grid item xs={12}>
                        <ControlledTextField name='comment' multiline rows={4} label={t('user:createUser.comment')} />
                      </Grid>
                      <Grid item xs={12}>
                        <UserDistrictAssociationDropdown preSelectedAssociation={preSelectedAssociation} preSelectedDistrict={preSelectedDistrict} />
                      </Grid>
                      {invoiceIsChecked() && (
                        <Grid item xs={12}>
                          <UserCreateInvoiceGroupSummary />
                        </Grid>
                      )}
                    </GridWrapper>
                  </Grid>
                  <Divider orientation='vertical' flexItem sx={{ ml: 3, mt: 3, display: { sm: 'none', md: 'flex' } }} />
                  <Grid item xs={12} md>
                    <Typography variant='caption'>{t('user:createUser.permissionHeader')}</Typography>
                    {sysProductFields.map((item, index) => {
                      const sysProd = sysProducts.find((x) => x.product_id === item.product_id);
                      return <UserCreateModalPermissionButton sysProduct={sysProd} key={item.product_id} index={index} authorityStatus={authority} />;
                    })}
                    <Box mt={2}>
                      <FormErrorSummary translationNamespace='user' errors={errors} />
                    </Box>
                  </Grid>
                </GridWrapper>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                <Button variant='outlined' sx={{ mr: 1 }} onClick={onHandleClose}>
                  {t('common:button.cancel')}
                </Button>
                <UserCreateConfirm loading={loading} onAbort={() => ConsoleHelper.log('')} onConfirm={() => handleSubmitClick()} />
              </Box>
            </form>
          </FormProvider>
        </FormLoaderWrapper>
      }
    />
  );
};
